import { useEffect, useRef, useState } from 'react';
import { IAddress } from 'utils/address';
import { AssignmentStatusEnum } from 'api/model';
import { useAssignmentCalculateFixedAssignmentTimeHook } from 'api/assignment/assignment';
import { AssignmentField } from './types';

interface Props {
  assignmentId: number;
  assignmentStatus: AssignmentStatusEnum;
  startAddress?: IAddress;
  stopOverAddress?: IAddress;
  endAddress?: IAddress;
  addingStopOver: boolean;
  /**
   * Case.isElectric. Elbilsärenden får aldrig fast tid från prislistan, oavsett rutt, så
   * uträkningen hoppas över helt (backend gör samma bedömning vid spar). Bara Efterkontroll och
   * SuperAdmin kan lägga in en fast tid för hand.
   */
  isElectric: boolean;
  /**
   * Prislistan sätter aldrig fast tid på benet: persontransport (tåg, buss, flyg, båt, taxi,
   * kollektivtrafik) — fast tid gäller bara ben där bilen faktiskt körs — och samåkningar, som
   * planeras på estimerad tid (bara Efterkontroll och SuperAdmin lägger in fast tid för hand).
   * Uträkningen hoppas över helt (backend gör samma bedömning vid spar).
   */
  noPriceListFixedTime: boolean;

  onChange: (
    assignmentId: number,
    field: AssignmentField,
    value: string
  ) => void;
}

const DEBOUNCE_MS = 300;

const useHandleChangedAddresses = ({
  assignmentId,
  assignmentStatus,
  startAddress,
  stopOverAddress,
  endAddress,
  addingStopOver,
  isElectric,
  noPriceListFixedTime,

  onChange,
}: Props): { isCalculatingFixedTime: boolean } => {
  const calculateFixedTime = useAssignmentCalculateFixedAssignmentTimeHook();
  const [isCalculatingFixedTime, setIsCalculatingFixedTime] = useState(false);

  // Drop stale results: if addresses change while a request is in flight, ignore
  // the older response so we don't overwrite the form with outdated data.
  const requestIdRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Adressnyckeln från förra körningen. Första körningen (null) är formulärets
  // öppning, inte en adressändring — då får ett liggande värde stå kvar även om
  // adresserna inte resolvar. Backend gör samma bedömning vid spar (isNew /
  // fromAddressChanged || toAddressChanged i CaseLogic.UpdateCaseAssignment).
  const lastAddressKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const addressKey = [
      startAddress?.name,
      startAddress?.address,
      startAddress?.city,
      startAddress?.zip,
      stopOverAddress?.name,
      stopOverAddress?.address,
      stopOverAddress?.city,
      stopOverAddress?.zip,
      endAddress?.name,
      endAddress?.address,
      endAddress?.city,
      endAddress?.zip,
      addingStopOver,
    ].join('|');
    const previousAddressKey = lastAddressKeyRef.current;
    lastAddressKeyRef.current = addressKey;
    const addressesChanged =
      previousAddressKey !== null && previousAddressKey !== addressKey;

    // do not update fixed time on approved assignments, and never on electric cases —
    // laddstoppen gör tidsåtgången ruttberoende, så prislistan sätter aldrig fast tid på
    // elbilsärenden (fältet styrs av useCaseAssignmentFormRows och av backend vid spar)
    const canCompute =
      !isElectric &&
      !noPriceListFixedTime &&
      assignmentStatus !== AssignmentStatusEnum.Approved &&
      !!startAddress &&
      !!endAddress &&
      (!addingStopOver || !!stopOverAddress);
    if (!canCompute) return undefined;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(async () => {
      requestIdRef.current += 1;
      const myRequestId = requestIdRef.current;
      setIsCalculatingFixedTime(true);

      try {
        if (addingStopOver && stopOverAddress) {
          const [startToStop, stopToEnd] = await Promise.all([
            calculateFixedTime({
              fromName: startAddress.name,
              fromAddress: startAddress.address,
              fromCity: startAddress.city,
              fromZip: startAddress.zip,
              toName: stopOverAddress.name,
              toAddress: stopOverAddress.address,
              toCity: stopOverAddress.city,
              toZip: stopOverAddress.zip,
            }),
            calculateFixedTime({
              fromName: stopOverAddress.name,
              fromAddress: stopOverAddress.address,
              fromCity: stopOverAddress.city,
              fromZip: stopOverAddress.zip,
              toName: endAddress.name,
              toAddress: endAddress.address,
              toCity: endAddress.city,
              toZip: endAddress.zip,
            }),
          ]);

          if (myRequestId !== requestIdRef.current) return; // stale

          // start → stopover: adresserna styr fast tid helt. Matchar båda företagen
          // sätts prisradens värde (eller tomt om prisrad saknas). Matchar de inte
          // (fritextadress) finns ingen fast tid mellan adresserna — då rensas fältet
          // också, men bara när adresserna faktiskt ändrats, så att formulärets
          // öppning inte blankar ett liggande värde. Backend gör samma rensning vid spar.
          if (startToStop.bothCompaniesMatched) {
            const ms = startToStop.fixedTimeInMilliseconds ?? null;
            const newValue = ms ? String(ms / 3600000) : '';
            onChange(
              assignmentId,
              AssignmentField.fixedAssignmentTimeInHours,
              newValue
            );
            if (ms) {
              onChange(
                assignmentId,
                AssignmentField.estimatedDuration,
                String(ms)
              );
            }
          } else if (addressesChanged) {
            onChange(
              assignmentId,
              AssignmentField.fixedAssignmentTimeInHours,
              ''
            );
          }

          // stopover → end: only set on a configured price; matches legacy
          // behavior of not clearing the stopover fields when no price exists.
          if (
            stopToEnd.bothCompaniesMatched &&
            stopToEnd.fixedTimeInMilliseconds
          ) {
            onChange(
              assignmentId,
              AssignmentField.stopOverFixedAssignmentTimeInHours,
              String(stopToEnd.fixedTimeInMilliseconds / 3600000)
            );
            onChange(
              assignmentId,
              AssignmentField.stopOverEstimatedDuration,
              String(stopToEnd.fixedTimeInMilliseconds)
            );
          }
        } else {
          const result = await calculateFixedTime({
            fromName: startAddress.name,
            fromAddress: startAddress.address,
            fromCity: startAddress.city,
            fromZip: startAddress.zip,
            toName: endAddress.name,
            toAddress: endAddress.address,
            toCity: endAddress.city,
            toZip: endAddress.zip,
          });

          if (myRequestId !== requestIdRef.current) return; // stale

          // Samma regel som i mellanlandningsgrenen ovan: matchar företagen sätts
          // prisradens värde, annars finns ingen fast tid mellan adresserna och
          // fältet rensas — dock bara vid en faktisk adressändring.
          if (result.bothCompaniesMatched) {
            const ms = result.fixedTimeInMilliseconds ?? null;
            const newValue = ms ? String(ms / 3600000) : '';
            onChange(
              assignmentId,
              AssignmentField.fixedAssignmentTimeInHours,
              newValue
            );
            if (ms) {
              onChange(
                assignmentId,
                AssignmentField.estimatedDuration,
                String(ms)
              );
            }
          } else if (addressesChanged) {
            onChange(
              assignmentId,
              AssignmentField.fixedAssignmentTimeInHours,
              ''
            );
          }
        }
      } catch {
        // Silent failure — leaving stale value is preferable to a UI error
        // since the user can still edit fast tid manually, and BE will
        // recompute at save-time as a backstop.
      } finally {
        if (myRequestId === requestIdRef.current) {
          setIsCalculatingFixedTime(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addingStopOver,
    assignmentId,
    assignmentStatus,
    isElectric,
    noPriceListFixedTime,
    startAddress?.name,
    startAddress?.address,
    startAddress?.city,
    startAddress?.zip,
    endAddress?.name,
    endAddress?.address,
    endAddress?.city,
    endAddress?.zip,
    stopOverAddress?.name,
    stopOverAddress?.address,
    stopOverAddress?.city,
    stopOverAddress?.zip,
  ]);

  return { isCalculatingFixedTime };
};

export default useHandleChangedAddresses;
